import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import type { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import {
  formatCircuitLayoutPickerLabel,
  formatCircuitPickerMenuLabel,
} from '../../lib/circuits';

type CircuitPickerEntry = ReturnType<typeof useSessionLapSlot>['circuitPickerOptions'][number];

type SessionCircuitPickerProps = {
  options: CircuitPickerEntry[];
  selectedContributionId: string;
  disabled?: boolean;
  onChange: (contributionId: string | null) => void;
  placeholder?: string;
};

export function SessionCircuitPicker({
  options,
  selectedContributionId,
  disabled = false,
  onChange,
  placeholder = 'Search all circuits…',
}: SessionCircuitPickerProps) {
  const groupedOptions = useMemo(() => {
    const venueGroups = new Map<string, CircuitPickerEntry[]>();
    for (const entry of options) {
      const venue = entry.contribution.venue?.trim() || 'Saved circuit';
      const group = venueGroups.get(venue) ?? [];
      group.push(entry);
      venueGroups.set(venue, group);
    }
    return [...venueGroups.values()].flat();
  }, [options]);
  const value = groupedOptions.find(
    (entry) => entry.contribution.id === selectedContributionId,
  ) ?? null;

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={groupedOptions}
      value={value}
      disabled={disabled}
      autoHighlight
      openOnFocus
      getOptionKey={(entry) => entry.contribution.id}
      getOptionLabel={(entry) => formatCircuitPickerMenuLabel(
        entry.contribution,
        { distanceKm: entry.distanceKm },
      )}
      groupBy={(entry) => entry.contribution.venue?.trim() || 'Saved circuit'}
      renderOption={(props, entry) => (
        <li {...props}>
          {formatCircuitLayoutPickerLabel(entry.contribution, { distanceKm: entry.distanceKm })}
        </li>
      )}
      isOptionEqualToValue={(option, selected) => (
        option.contribution.id === selected.contribution.id
      )}
      onChange={(_, entry) => onChange(entry?.contribution.id ?? null)}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Search and select a circuit',
          }}
        />
      )}
    />
  );
}
